/**
 * Client types and calls for database connections (4 Oct 2026).
 *
 * The server keeps no copy of a database connection: the connection, its credential and its
 * runner live in Coherence, and `GET /api/database-connections` reads them back as the signed-in
 * person, who only ever sees the connections they made (src/api/handlers/database-connections.ts).
 * Connecting is ONE call carrying the coordinates and the password; there is no create-then-verify,
 * no sharing choice and no table review — the structure is read live when a question needs it.
 */

import { api } from "./api";
import { announceAppsChanged } from "./apps-changed";

export interface DatabaseConnection {
  id: string;
  /** The name the person gave it, or a description built from where it points. */
  label: string;
  named?: boolean;
  engine?: string | null;
  host: string | null;
  port: number | null;
  database: string | null;
  schema: string | null;
  username: string | null;
  /**
   * - `live`: it has a runner and Coherence does not refuse it.
   * - `not_ready`: no runner — the database refused the last password, or it never finished.
   *   Giving it a working password is the remedy.
   * - `unhealthy`: it has a runner and Coherence says it should not be used.
   */
  state: "live" | "not_ready" | "unhealthy";
  last_verified_at: string | null;
  created_at: string | null;
  /** Everything listed is the person's own, so they may remove it. */
  can_remove?: boolean;
}

export interface DatabaseRefusalCard {
  type: string;
  unknown: string[];
  documented_but_absent: string[];
  narrowed_away: string[];
  documented_hint: string | null;
  headline: string;
  detail: string;
  attempted_sql: string | null;
  reject_reason: string | null;
}

export interface DatabaseAskResponse {
  status: "answered" | "refused";
  sql: string | null;
  answer: string | null;
  rows: unknown[][] | null;
  columns: string[] | null;
  row_count: number | null;
  truncated: boolean;
  refusal: DatabaseRefusalCard | null;
  schema_version: number | null;
  elapsed_ms: number;
  attempts: number;
  audit_id: string;
  fixture_disclaimer: string | null;
}

/** What a connect, a check or a password update measured. */
export interface DatabaseConnectionMeasurement {
  id?: string;
  state?: string;
  read_only_verified?: boolean;
  observed_object_count?: number;
  verification_scope?: string;
  write_capable?: boolean;
  credential_replaced?: boolean;
}

export interface DatabaseConnectBody {
  connection_id?: string;
  label: string;
  engine: string;
  host: string;
  port: number;
  database: string;
  schema?: string;
  username: string;
  password: string;
  credential_form?: Record<string, string>;
}

export const DATABASE_CONNECTIONS_PATH = "/api/database-connections";

function withConversation(path: string, conversationId?: string): string {
  return conversationId ? `${path}?conversation_id=${encodeURIComponent(conversationId)}` : path;
}

/** Announce, after the server answered, that her apps changed (lib/apps-changed.ts). */
async function thenAppsChanged<T>(request: Promise<T>): Promise<T> {
  const result = await request;
  announceAppsChanged();
  return result;
}

export const databaseConnectionsApi = {
  list: () => api.get<{ connections: DatabaseConnection[] }>(DATABASE_CONNECTIONS_PATH),
  /** Connect: Coherence provisions it, the server measures it. The password goes here only. */
  connect: (body: DatabaseConnectBody, conversationId?: string) =>
    thenAppsChanged(
      api.post<DatabaseConnectionMeasurement>(
        withConversation(DATABASE_CONNECTIONS_PATH, conversationId),
        body,
      ),
    ),
  check: (id: string, conversationId?: string) =>
    api.post<DatabaseConnectionMeasurement>(
      withConversation(
        `${DATABASE_CONNECTIONS_PATH}/${encodeURIComponent(id)}/check`,
        conversationId,
      ),
    ),
  /** SYN-3163. A NEW password for an existing connection: same connection, same runner address. */
  updatePassword: (
    id: string,
    password: string,
    opts: { conversationId?: string; credentialForm?: Record<string, string> } = {},
  ) =>
    thenAppsChanged(
      api.post<DatabaseConnectionMeasurement>(
        withConversation(
          `${DATABASE_CONNECTIONS_PATH}/${encodeURIComponent(id)}/password`,
          opts.conversationId,
        ),
        opts.credentialForm ? { password, credential_form: opts.credentialForm } : { password },
      ),
    ),
  /** Remove: the governed Disconnect. 204 when done. */
  remove: (id: string) =>
    thenAppsChanged(api.delete<void>(`${DATABASE_CONNECTIONS_PATH}/${encodeURIComponent(id)}`)),
  ask: (id: string, question: string) =>
    api.post<DatabaseAskResponse>(`${DATABASE_CONNECTIONS_PATH}/${encodeURIComponent(id)}/ask`, {
      question,
    }),
};
